import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

const SSO_ENABLED = true;

export enum SsoEnvironment {
    DEV = 'dev',
    ETHAN_LAB = 'ethan-lab',
    INTEGRATION = 'integration',
    PRODUCTION = 'production',
    REF_LAB = 'ref-lab',
    STAGE = 'stage',
}

type SsoEnvironmentConfig = {
    cognitoClientId: string;
    cognitoDomain: string;
    cognitoUserPoolId: string;
    logoutUri: string;
    redirectUri: string;
};

const SSO_ENVIRONMENTS: Record<SsoEnvironment, SsoEnvironmentConfig> = {
    [SsoEnvironment.DEV]: {
        cognitoClientId: '2bb21qe0857mru3eo944m56erh',
        cognitoDomain: 'https://softcat-main-666258711639.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_W3VYrfEGU',
        logoutUri: 'https://secure-softcat.dev.services.skyq.sky.com/console/login',
        redirectUri: 'https://secure-softcat.dev.services.skyq.sky.com/console/auth/callback',
    },
    [SsoEnvironment.INTEGRATION]: {
        cognitoClientId: '298hmg546m0jrih73e9p7caaq7',
        cognitoDomain: 'https://softcat-integration-746859878310.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_vmJZMFEj4',
        logoutUri: 'https://secure-softcat.integration.services.skyq.sky.com/console/login',
        redirectUri:
            'https://secure-softcat.integration.services.skyq.sky.com/console/auth/callback',
    },
    [SsoEnvironment.REF_LAB]: {
        cognitoClientId: 'pkpt5g765f2m0s15avcanfu1j',
        cognitoDomain: 'https://softcat-ref-lab-746859878310.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_lBk2WbXfG',
        logoutUri: 'https://secure-softcat.ref-lab.services.skyq.sky.com/console/login',
        redirectUri: 'https://secure-softcat.ref-lab.services.skyq.sky.com/console/auth/callback',
    },
    [SsoEnvironment.ETHAN_LAB]: {
        cognitoClientId: '1qdf85n3ju5hgjcfv5ip4bvus8',
        cognitoDomain: 'https://softcat-ethan-lab-746859878310.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_zQRTpyWKC',
        logoutUri: 'https://secure-softcat.ethan-lab.services.skyq.sky.com/console/login',
        redirectUri: 'https://secure-softcat.ethan-lab.services.skyq.sky.com/console/auth/callback',
    },
    [SsoEnvironment.STAGE]: {
        cognitoClientId: '46vranm5ggthls6laim2iv6ppi',
        cognitoDomain: 'https://softcat-staging-746859878310.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_Mcy6G2PNs',
        logoutUri: 'https://secure-softcat.stage.services.skyq.sky.com/console/login',
        redirectUri: 'https://secure-softcat.stage.services.skyq.sky.com/console/auth/callback',
    },
    [SsoEnvironment.PRODUCTION]: {
        cognitoClientId: '6las11kn1m31ju2cpvfuuged6t',
        cognitoDomain: 'https://softcat-prod-953246180274.auth.eu-west-1.amazoncognito.com',
        cognitoUserPoolId: 'eu-west-1_I1fBieMmr',
        logoutUri: 'https://secure-softcat.services.skyq.sky.com/console/login',
        redirectUri: 'https://secure-softcat.services.skyq.sky.com/console/auth/callback',
    },
};

export const getSsoEnvironment = (host: string): SsoEnvironment => {
    for (const environment of Object.values(SsoEnvironment)) {
        if (host.includes(environment)) {
            return environment;
        }
    }

    return SsoEnvironment.PRODUCTION;
};

/* v8 ignore start */
const isLocalhost = globalThis.location?.hostname?.includes('localhost') ?? false;

const environment = isLocalhost
    ? {
          cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
          cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
          cognitoLogin: false,
          cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
          logoutUri: import.meta.env.VITE_LOGOUT_URI ?? '',
          redirectUri: import.meta.env.VITE_REDIRECT_URI ?? '',
      }
    : SSO_ENVIRONMENTS[getSsoEnvironment(globalThis.location?.hostname ?? '')];
/* v8 ignore stop */

const SKY_SSO_PROVIDER_NAME = 'Sky-SSO';

type RuntimeSsoConfig = SsoEnvironmentConfig & {
    // Base URL of the Softcat API, for deployments where the API is on another origin than the app.
    apiBaseUrl?: string;
    // Sign in with the user pool's own login form (username and password) instead of Sky SSO.
    cognitoLogin?: boolean;
};

const toSsoConfig = (config: RuntimeSsoConfig) => {
    return {
        COGNITO_AUTHORITY: `https://cognito-idp.eu-west-1.amazonaws.com/${config.cognitoUserPoolId}`,
        COGNITO_CLIENT_ID: config.cognitoClientId,
        COGNITO_DOMAIN: config.cognitoDomain,
        LOGOUT_URI: config.logoutUri,
        REDIRECT_URI: config.redirectUri,
        SKY_SSO_PROVIDER_NAME,
        SSO_ENABLED,
        USE_COGNITO_LOGIN: config.cognitoLogin === true,
    };
};

export const SsoConfig = toSsoConfig(environment);

const RUNTIME_CONFIG_FILE = 'config.json';
const REQUIRED_RUNTIME_CONFIG_KEYS = [
    'cognitoClientId',
    'cognitoDomain',
    'cognitoUserPoolId',
    'logoutUri',
    'redirectUri',
] as const;

const isRuntimeConfig = (value: unknown): value is RuntimeSsoConfig => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const record = value as Record<string, unknown>;

    const hasRequiredStrings = REQUIRED_RUNTIME_CONFIG_KEYS.every((key) => {
        return typeof record[key] === 'string' && record[key] !== '';
    });

    return hasRequiredStrings && (record.cognitoLogin === undefined || typeof record.cognitoLogin === 'boolean');
};

/**
 * Overrides the built-in SSO settings with the `config.json` published next to `index.html` by the
 * deployment, when there is one. Stages deployed without it (or hosts that answer with HTML for a
 * missing file) keep the built-in settings. Must finish before anything reads `SsoConfig` or calls
 * the API.
 */
export const loadRuntimeSsoConfig = async (): Promise<void> => {
    let runtimeConfig: unknown;

    try {
        const response = await fetch(`${import.meta.env.BASE_URL}${RUNTIME_CONFIG_FILE}`);
        if (response.ok) {
            runtimeConfig = await response.json();
        }
    } catch {
        runtimeConfig = undefined;
    }

    if (isRuntimeConfig(runtimeConfig)) {
        Object.assign(SsoConfig, toSsoConfig(runtimeConfig));

        if (typeof runtimeConfig.apiBaseUrl === 'string' && runtimeConfig.apiBaseUrl !== '') {
            SoftcatApiUtils.setBaseUrl(runtimeConfig.apiBaseUrl);
        }
    }
};
